/**
 * Bočný panel „Súbojová fáza" (docs/design/5-hra.png). Kým scéna nad doskou
 * je divadlo na pár sekúnd, tento panel drží stav súboja celý čas: kto útočí
 * na koho, v akom kroku sa súboj nachádza a čo sa od hráča čaká.
 *
 * Karty ukazuje len vtedy, keď ich ukázala aj scéna (po odhalení) — pred
 * odhalením je útočníkova karta skrytá aj v stave, ktorý server posiela.
 */

import type { CardDef, PendingChoice } from '@unmatched/engine';
import { portraitCard } from '@unmatched/content';
import type React from 'react';

import type { CombatShow, FighterSnapshot } from '../game/useStage.js';
import { Card } from './Card.js';
import { cardImageUrl } from '../cardImage.js';

interface Props {
  combat: CombatShow | null;
  pending: PendingChoice | null;
  viewer: number;
  nameOf: (player: number) => string;
  defOf: (cardId: string) => CardDef | null;
  /** Spidey-Sense: útočník ohlásil vytlačenú hodnotu ešte pred odhalením. */
  announced?: number;
}

const STEP_LABEL: Record<CombatShow['phase'], string> = {
  declared: 'obrana',
  flip: 'odhalenie',
  values: 'hodnoty',
  result: 'vyhodnotenie',
};

export function CombatPanel({ combat, pending, viewer, nameOf, defOf, announced }: Props) {
  if (!combat && !pending) return null;

  if (!combat && pending) {
    const mine = pending.player === viewer;
    return (
      <section className="sidePanel sidePanel--effect">
        <header className="sidePanel__head">
          <span className="sidePanel__title">
            <span className="dot dot--primary" /> Efekt karty
          </span>
          <span className="sidePanel__meta">{mine ? 'rozhoduješ ty' : nameOf(pending.player)}</span>
        </header>
        <p className="sidePanel__lead">{pending.label}</p>
        <p className="muted">
          {mine
            ? {
                move: 'Vyber figúrku a klikni, kam ju presunúť.',
                place: 'Klikni na zvýraznený priestor.',
                damage: 'Klikni na figúrku, ktorú to zasiahne.',
                confirm: 'Potvrď alebo odmietni tlačidlom dole.',
                token: 'Klikni na priestor s hmlou a potom na cieľ.',
                placeToken: 'Klikni na zvýraznený priestor.',
                card: 'Vyber kartu.',
                option: 'Vyber jednu z možností dole.',
                swap: 'Klikni na figúrku, s ktorou sa vymeniť, alebo odmietni.',
                auto: '',
                bonusAttack: '',
              }[pending.kind]
            : 'Čaká sa na rozhodnutie súpera.'}
        </p>
      </section>
    );
  }

  const show = combat!;
  const revealed = show.phase !== 'declared';
  const attackDef = revealed && show.attackCard ? defOf(show.attackCard) : null;
  const defenseDef = revealed && show.defenseCard ? defOf(show.defenseCard) : null;
  const iDefend = show.defender.owner === viewer && !revealed;

  return (
    <section className="sidePanel sidePanel--combat">
      <header className="sidePanel__head">
        <span className="sidePanel__title">
          <span className="dot dot--danger" /> Súbojová fáza
        </span>
        <span className="sidePanel__meta">{STEP_LABEL[show.phase]}</span>
      </header>

      <div className="clash">
        <ClashSide
          label={show.bonus ? `Bonus: ${show.bonus}` : `Útočník: ${show.attacker.name}`}
          fighter={show.attacker}
          tone="attack"
        >
          {attackDef ? (
            <Card def={attackDef} />
          ) : (
            <div className="card card--back" aria-label="Karta lícom dole" />
          )}
          {!revealed && announced !== undefined ? (
            <div className="clash__announced">ohlásená hodnota {announced}</div>
          ) : null}
        </ClashSide>
        <ClashSide
          label={show.defender.owner === viewer ? 'Tvoja obrana' : `Obranca: ${show.defender.name}`}
          fighter={show.defender}
          tone="defense"
        >
          {revealed ? (
            defenseDef ? (
              <Card def={defenseDef} />
            ) : (
              <div className="clash__none">bez obrany</div>
            )
          ) : iDefend ? (
            <div className="clash__ask">
              <b>Vyber kartu</b>
              <span>z ruky, alebo „Nebrániť sa"</span>
            </div>
          ) : (
            <div className="card card--back is-waiting" aria-label="Obranca vyberá" />
          )}
        </ClashSide>
      </div>

      <footer className="clash__foot">
        {show.phase === 'result' || show.phase === 'values' ? (
          <>
            <span>
              {show.attackValue ?? 0} − {show.defenseValue ?? 0}
            </span>
            <b className={show.damage ? 'is-hit' : ''}>
              {show.defeated
                ? `${show.defender.name} — mimo boja`
                : show.damage
                  ? `${show.damage} poškodenia`
                  : 'útok odrazený'}
            </b>
          </>
        ) : (
          <>
            <span>{nameOf(show.attacker.owner)} útočí</span>
            <b>{revealed ? 'odhaľuje sa…' : 'karty sú lícom dole'}</b>
          </>
        )}
      </footer>
    </section>
  );
}

function ClashSide({
  label,
  fighter,
  tone,
  children,
}: {
  label: string;
  fighter: FighterSnapshot;
  tone: 'attack' | 'defense';
  children: React.ReactNode;
}) {
  const card = portraitCard(fighter.defId);
  return (
    <div className={`clash__side clash__side--${tone} clash__side--p${fighter.owner}`}>
      <span className="clash__label">{label}</span>
      <span className="clash__who">
        <span className="clash__portrait">
          {card ? (
            <img src={cardImageUrl(card)} alt="" draggable={false} />
          ) : (
            fighter.name.slice(0, 1)
          )}
        </span>
        <span className="clash__hp">
          ♥ {fighter.hp}
          <small>/{fighter.maxHp}</small>
        </span>
      </span>
      <div className="clash__card">{children}</div>
    </div>
  );
}
